import {
  AvailableIntegrationFragment,
  ConnectionBaseFragment,
} from "@/apollo/types";
import { ActionButton } from "@/components/elements/Button";
import IconWithBG from "@/components/elements/IconWithBG";
import { List } from "@/components/elements/List";
import { Heading, TextMuted } from "@/components/elements/Typography";
import { ReverseEtlIcon } from "@/components/icons/outline";
import { useRecommendedIntegrationsByBusinessDepartment } from "@/features/connectors";
import { useConnectionsByIntegrationId } from "@/features/connectors/lib/useConnectionsByIntegrationId";
import cn from "@/helpers/classNames";
import { IntegrationLogoBox } from "@/integrations";
import { useNavigateWithSlug } from "@/routes";
import { Listbox } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { ComponentProps } from "@tw-classed/react";

export function RevEltSyncsEmptyState() {
  const navigate = useNavigateWithSlug();

  // const { integrations: onboardingConnectionPreferences, loading } =
  //   useOnboardingConnectionPreferences(IntegrationAbility.ActivateDestination);

  const { integrations } = useRecommendedIntegrationsByBusinessDepartment();

  // let integrations = onboardingConnectionPreferences;
  // if (
  //   !onboardingConnectionPreferences ||
  //   onboardingConnectionPreferences.length === 0
  // ) {
  //   integrations = recommendedIntegrations;
  // }

  return (
    <div className="flex w-full justify-center">
      <div className="flex flex-col items-center gap-6">
        <div className="text-center">
          <Heading className="mb-4 text-xl">
            Activate Your Data with Reverse ETL
          </Heading>
          <TextMuted className="">
            Start using your data effectively by sending it back to your
            favorite SaaS tools.
          </TextMuted>
        </div>
        <div className="flex flex-1 flex-col gap-6">
          {/* {loading ? (
            <div className="divide-y rounded border">
              {range(4).map((i) => (
                <div key={i} className="flex h-14 items-center gap-3 px-4">
                  <div className="flex h-10 w-10 items-center">
                    <Skeleton className="h-4 w-full" />
                  </div>
                  <Skeleton className="h-4 grow" />
                </div>
              ))}
            </div>
          ) : ( */}
          <div className="max-h-[21.4rem] overflow-hidden">
            <DataSourceSuggestionsList
              className="h-full"
              integrations={integrations}
              onSelectItem={(integration, connection) =>
                navigate({
                  to: "/reverse-etl/new",
                  search: {
                    integration_id: integration.id,
                    connection_id: connection?.id,
                  },
                })
              }
            />
          </div>
          {/* )} */}
          <div className="shrink-0">
            <ExploreMoreButton />
          </div>
        </div>
      </div>
    </div>
  );
}

function ExploreMoreButton() {
  const navigate = useNavigateWithSlug();
  return (
    <ActionButton
      variant="outline"
      colorScheme="secondary"
      onClick={() => {
        navigate({
          to: "/reverse-etl/new",
          search: {
            integration_id: undefined,
          },
        });
      }}
      className="flex w-full gap-3 px-4 shadow"
    >
      <IconWithBG icon={<ReverseEtlIcon />} size="md" />
      <span>Explore More Destinations</span>
    </ActionButton>
  );
}

function DataSourceSuggestionsList({
  integrations,
  onSelectItem,
  ...props
}: {
  integrations: AvailableIntegrationFragment[];
  onSelectItem: (
    integration: AvailableIntegrationFragment,
    connection?: ConnectionBaseFragment,
  ) => void;
} & ComponentProps<typeof List>) {
  const existingConnectionsByIntegrationId = useConnectionsByIntegrationId();
  return (
    <List
      {...props}
      className={cn(
        "w-auto gap-2 divide-y rounded border p-0",
        props.className,
      )}
    >
      {integrations.map((integration) => {
        const existingConnections =
          existingConnectionsByIntegrationId.get(integration.id) ?? [];
        if (existingConnections.length > 0) {
          return (
            <Listbox
              key={integration.id}
              onChange={(value: ConnectionBaseFragment) => {
                onSelectItem(integration, value);
              }}
            >
              <Listbox.Button as="div">
                <List.ItemButton
                  key={integration.id}
                  className="flex w-full items-center gap-3"
                >
                  <IntegrationLogoBox id={integration.id} size="md" />
                  <div className="text-sm">{integration.name}</div>
                </List.ItemButton>
              </Listbox.Button>
              <Listbox.Options
                as={"ul"}
                className="absolute z-50 rounded-md bg-white text-base shadow-xl outline-2 outline-blue-500 ring-1 ring-black ring-opacity-5 focus-within:outline focus:outline dark:bg-gray-800 dark:ring-gray-600"
              >
                <div className="py-1">
                  <Listbox.Option
                    as="li"
                    value={undefined}
                    className={({ active }) =>
                      cn(
                        "flex cursor-default select-none items-center gap-4 px-4 py-2",
                        {
                          "bg-blue-500 text-white": active,
                          "bg-white dark:bg-gray-800 dark:text-white": !active,
                        },
                      )
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectItem(integration);
                    }}
                  >
                    <PlusIcon className="h-5 w-5" /> Create new connection
                  </Listbox.Option>
                </div>
                <div className="flex items-center border-y bg-muted px-4 py-2 text-muted-foreground">
                  Or select an existing connection
                </div>
                <div className="max-h-60 divide-y overflow-auto py-1">
                  {existingConnections.map((connection, i) => (
                    <Listbox.Option
                      key={connection.id}
                      as="li"
                      value={connection}
                      className={({ active }) =>
                        cn(
                          "flex cursor-default select-none items-center gap-4 px-4 py-2",
                          {
                            "bg-blue-500 text-white": active,
                            "bg-white dark:bg-gray-800 dark:text-white":
                              !active,
                          },
                        )
                      }
                    >
                      <IntegrationLogoBox
                        id={integration.id}
                        className="h-5 w-5"
                      />{" "}
                      {connection.label}
                    </Listbox.Option>
                  ))}
                </div>
              </Listbox.Options>
            </Listbox>
          );
        }
        return (
          <List.ItemButton
            key={integration.id}
            className="flex w-full items-center gap-3"
            onClick={() => onSelectItem(integration)}
          >
            <IntegrationLogoBox id={integration.id} size="md" />
            <div className="text-sm">{integration.name}</div>
          </List.ItemButton>
        );
      })}
    </List>
  );
}
